import { get } from '@ember/helper';
import { on } from '@ember/modifier';
/* eslint-disable ember/no-runloop */
import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBadge,
  PrismIcon,
  PrismMetricBlock,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import GeneralDropdownsDropdownArrowButton from 'lumen/components/general/dropdowns/dropdown-arrow-button';
import HotelOverviewDashboardFeedFeedEntryDemandLevelEntry from 'lumen/components/hotel/overview-dashboard/feed/feed-entry/demand-level-entry';
import HotelOverviewDashboardFeedKeyDatesNote from 'lumen/components/hotel/overview-dashboard/feed/key-dates-note';
import dayjsFormat from 'lumen/helpers/dayjs-format';
import scrollIntoView from 'lumen/modifiers/scroll-into-view';
import {
  OD_ALERTS_KEYS,
  OD_ALERTS_SORT_ORDER,
  OD_ALERTS_TO_TRANSLATION_KEY_MAPPING,
  OD_KEY_DATES_SOURCES,
  OD_SETTINGS_KEYS,
} from 'lumen/utils/constants/overview-dashboard-constants';

interface HotelOverviewDashboardFeedFeedEntrySignature {
  Args: {
    data: unknown;
    isAlertsRequestLoading: unknown;
    onKeyDateStarClicked: unknown;
    relaunchAlertsDataRequest: unknown;
    toggleOrSetSelectedDay: unknown;
    triggerDayDrilldownOutsideSelectedRange: unknown;
    updateKeyDateNoteTask: unknown;
  };
}

export default class HotelOverviewDashboardFeedFeedEntry extends Component<HotelOverviewDashboardFeedFeedEntrySignature> {
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare odSelections: Services['od-selections'];

  @tracked isExtraAlertsExpanded = false;

  // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView
  scrollIntoViewOptions = { block: 'nearest', inline: 'start' };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get feedDateFormat() {
    return this.dates.partialDateFormat?.WEEKDAY_AND_DATE_MEDIUM;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedAlerts() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.args.data.alerts.length) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      const formattedAlertsList = this.args.data.alerts
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .reduce((filteredAlertsList, alert) => {
          let formattedName = '';

          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (alert.name === OD_ALERTS_KEYS.compset_change) {
            formattedName = this.intl.t(
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              OD_ALERTS_TO_TRANSLATION_KEY_MAPPING[alert.name],
              {
                htmlSafe: true, // Required because the translation parameter is the result of our formatRate util function
                threshold: this.hotelAndMarketService.formatRate(
                  // @ts-expect-error: Incorrect type
                  this.hotelAndMarketService.hotel.overviewSettings[
                    OD_SETTINGS_KEYS.compset_change_threshold
                  ],
                  { longFormat: false },
                ),
              },
            );
          } else {
            formattedName = this.intl.t(
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              OD_ALERTS_TO_TRANSLATION_KEY_MAPPING[alert.name],
            );
          }

          const formattedAlert = this.formatAlertValue(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            alert.name,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            alert.value,
            this.args.data,
          );

          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          filteredAlertsList.push({
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            id: alert.name,
            name: formattedName,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            value: formattedAlert.formattedValue,
            class: formattedAlert.formattedClass,
          });

          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return filteredAlertsList;
        }, [])
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .sort(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          (a, b) => OD_ALERTS_SORT_ORDER[a.id] - OD_ALERTS_SORT_ORDER[b.id],
        );

      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        defaultAlerts: formattedAlertsList.slice(0, 3),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        extraAlerts: formattedAlertsList.slice(3, formattedAlertsList.length),
      };
    }

    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasExtraAlerts() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return Boolean(this.formattedAlerts.extraAlerts?.length);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasNote() {
    // Note: after discussions we've decided to only show note badge/note value on expand in the keydate feed
    // Currently there's always an entry in the notes array, so we need to check if the text property contains something
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      this.isKeyDateFeedEntry &&
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.args.data.notes.some((note) => note.text !== '')
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasNoteSection() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      this.isKeyDateFeedEntry &&
      (this.hasNote || this.isFeedEntryDayDrilledDown)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isFeedEntryDayDrilledDown() {
    return (
      // @ts-expect-error: Incorrect type
      this.args.data.fromDate === this.odSelections.activeDrilldownDayAsString
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isKeyDateFeedEntry() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.data.isKeyDate;
  }

  // Getter used to trigger the scroll-into-view modifier
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get shouldScrollIntoView() {
    // We only want to scroll a feed-entry into view if it's expanded
    if (this.isFeedEntryDayDrilledDown) {
      return new Promise((resolve) => {
        // We use next() here to postpone the scrollIntoView execution.
        // If executed too early the scrollIntoView will be off, as probably not all feed-entries have been rendered yet.
        // Not the cleanest solution (but cleaner than e.g. setTimeout(0)), but there is no easy way currently to handle that
        next(this, () => resolve(true));
      });
    }

    return false;
  }

  // Fixed options for the scroll into view functionality (currently using ember-scroll-modifiers - based upon scrollIntoView browser API)
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  static formatAlertChangeValue(number) {
    let className = '';

    if (number) {
      if (number > 0) {
        className = 'text-success-default';
      } else if (number < 0) {
        className = 'text-critical-default';
      }
    }

    return className;
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatAlertValue(name, value, alertData) {
    const alertsWithPercentages = [
      OD_ALERTS_KEYS.otb_pickup,
      OD_ALERTS_KEYS.market_otb_pickup,
      OD_ALERTS_KEYS.short_term_rental_otb_pickup,
    ];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    if (alertsWithPercentages.includes(name)) {
      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        formattedValue: this.intl.formatNumber(value, {
          style: 'percent',
          signDisplay: 'always',
        }),
        formattedClass:
          HotelOverviewDashboardFeedFeedEntry.formatAlertChangeValue(value),
      };
    }

    const alertsWithRates = [
      OD_ALERTS_KEYS.adr_pickup,
      OD_ALERTS_KEYS.short_term_rental_rate_pickup,
    ];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    if (alertsWithRates.includes(name)) {
      const isADRAlert = name === OD_ALERTS_KEYS.adr_pickup;
      return {
        formattedValue: this.hotelAndMarketService.formatRate(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          value,
          { includeSign: true, useOverviewRevenueCurrency: isADRAlert },
        ),
        formattedClass:
          HotelOverviewDashboardFeedFeedEntry.formatAlertChangeValue(value),
      };
    }

    if (name === OD_ALERTS_KEYS.price_position_change) {
      // One exception is needed to format the price position change
      // In case there is an alert message (soldout, LOS restriction,...), we can't show the price position change value
      // Similar behavior to the formatted price position we show in the card
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (!alertData.currentPriceMessage) {
        return {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          formattedValue: this.intl.formatNumber(value, {
            signDisplay: 'always',
          }),
          formattedClass:
            HotelOverviewDashboardFeedFeedEntry.formatAlertChangeValue(value),
        };
      }

      // Display sold out in case of extra message
      return {
        formattedValue: this.intl.t('general.soldout'),
        formattedClass: '',
      };
    }

    if (name === OD_ALERTS_KEYS.price_opportunity) {
      const opportunityChange = value > 0 ? 'higher' : 'lower';
      const opportunityMessage = this.intl.t(
        `market_insight.${opportunityChange}_than_expected`,
      );

      return {
        formattedValue: opportunityMessage,
        formattedClass: '',
      };
    }

    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      formattedValue: value,
      formattedClass: '',
    };
  }

  @action
  expandClicked(): void {
    // Note: we're currently testing having a separate expand behaviour, depending if we're dealing with a key date or alert
    // Main reason is that most things in the dashboard (and drilling down on a day) is based on data within the selected daterange (the range of the datepicker on the page)
    // Alerts are only returned for the selected range, but key dates can be added freely by the user, and are always shown to the user
    // This means we can run into issues if a user adds a key date outside of the selected date range => this means we need a separate behaviour in case a key date is clicked
    if (this.isKeyDateFeedEntry) {
      // @ts-expect-error: Incorrect type
      this.args.triggerDayDrilldownOutsideSelectedRange(
        // @ts-expect-error: Incorrect type
        this.args.data.fromDate,
      );
    } else {
      // @ts-expect-error: Incorrect type
      this.args.toggleOrSetSelectedDay(this.args.data.fromDate);
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  onFeedStarClicked(e): void {
    // We don't want the star click to trigger the expand, so we stop the event propagation here
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    e.stopPropagation();

    // @ts-expect-error: Incorrect type
    this.args.onKeyDateStarClicked(
      this.args.data,
      '',
      OD_KEY_DATES_SOURCES.alert,
    );
  }

  @action
  // @ts-expect-error: Incorrect type
  toggleExtraAlertsVisibility(e): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    e.stopPropagation();
    this.isExtraAlertsExpanded = !this.isExtraAlertsExpanded;
  }

  <template>
    {{! @glint-nocheck }}
    {{! template-lint-disable no-nested-interactive require-presentational-children }}
    <div
      class="od-feed-entry
        {{if this.isFeedEntryDayDrilledDown 'od-feed-entry--drilled-down-day'}}"
      {{scrollIntoView
        options=this.scrollIntoViewOptions
        shouldScroll=this.shouldScrollIntoView
      }}
    >
      <div
        class="od-feed-entry__alerts"
        role="button"
        {{on "click" this.expandClicked}}
      >
        <div class="flex items-center justify-between">
          <span class="flex flex-row items-center">
            <h3>{{dayjsFormat @data.fromDate format=this.feedDateFormat}}</h3>

            {{#if @data.events.length}}
              <div class="ml-200">
                <PrismBadge
                  @iconName="calendar-accepted"
                  @label={{@data.events.length}}
                />
              </div>
            {{/if}}
          </span>

          <button
            class="button--unstyled mr-100"
            type="button"
            {{on "click" this.onFeedStarClicked}}
          >
            {{#if @data.isKeyDate}}
              <div class="icon-star--key-dates">
                <PrismIcon @color="inherit" @name="star-filled" @size="small" />
              </div>
            {{else}}
              <PrismIcon @name="star-outline" @size="small" />
            {{/if}}
          </button>
        </div>

        {{#if this.formattedAlerts.defaultAlerts}}
          <div>
            <h4>
              {{t "overview_dashboard.feed.alerts"}}
            </h4>

            <div class="od-feed-entry-alerts-grid-layout mt-200">
              {{#each this.formattedAlerts.defaultAlerts as |alert|}}
                <div class="od-feed-entry__alert">
                  <PrismMetricBlock
                    @allowLabelTextWrapping={{true}}
                    @compact={{true}}
                  >
                    <:value>
                      <p class={{alert.class}}>
                        {{#if
                          (eq alert.id OD_ALERTS_KEYS.average_demand_level)
                        }}
                          <HotelOverviewDashboardFeedFeedEntryDemandLevelEntry
                            @value={{alert.value}}
                          />
                        {{else}}
                          {{alert.value}}
                        {{/if}}
                      </p>
                    </:value>
                    <:label>
                      <span class="text-200--regular">
                        {{alert.name}}
                      </span>
                    </:label>
                  </PrismMetricBlock>
                </div>
              {{/each}}
            </div>
          </div>
        {{/if}}

        {{#if this.hasExtraAlerts}}
          <button
            class="button--unstyled flex flex-row items-center pt-100 pb-100"
            type="button"
            {{on "click" this.toggleExtraAlertsVisibility}}
          >
            <GeneralDropdownsDropdownArrowButton
              @isOpen={{this.isExtraAlertsExpanded}}
            />
            <p class="text-300--regular ml-200">
              {{t
                "overview_dashboard.feed.alert.expand_label"
                count=this.formattedAlerts.extraAlerts.length
              }}
            </p>
          </button>

          {{#if this.isExtraAlertsExpanded}}
            <div class="od-feed-entry-alerts-grid-layout">
              {{#each this.formattedAlerts.extraAlerts as |alert|}}
                <div class="od-feed-entry__alert">
                  <PrismMetricBlock
                    @allowLabelTextWrapping={{true}}
                    @compact={{true}}
                  >
                    <:value>
                      <p class={{alert.class}}>
                        {{#if
                          (eq alert.id OD_ALERTS_KEYS.average_demand_level)
                        }}
                          <HotelOverviewDashboardFeedFeedEntryDemandLevelEntry
                            @value={{alert.value}}
                          />
                        {{else}}
                          {{alert.value}}
                        {{/if}}
                      </p>
                    </:value>
                    <:label>
                      <span class="text-200--regular">
                        {{alert.name}}
                      </span>
                    </:label>
                  </PrismMetricBlock>
                </div>
              {{/each}}
            </div>
          {{/if}}
        {{/if}}
      </div>

      {{#if this.hasNoteSection}}
        <div class="od-feed-entry__note-section flex flex-col">
          <HotelOverviewDashboardFeedKeyDatesNote
            @data={{get @data.notes "0"}}
            @hasNote={{this.hasNote}}
            @isAlertsRequestLoading={{@isAlertsRequestLoading}}
            @isFeedEntryDayDrilledDown={{this.isFeedEntryDayDrilledDown}}
            @keyDateFromDate={{@data.fromDate}}
            @relaunchAlertsDataRequest={{@relaunchAlertsDataRequest}}
            @updateKeyDateNoteTask={{@updateKeyDateNoteTask}}
          />
        </div>
      {{/if}}
    </div>
  </template>
}
